import { RESUME_PREFIX, USER_ID_KEY } from '$lib/global/constants';

import { clearDeferredIntegrationEvents } from '$lib/global/tracking/deferredIntegrationEvents';

import Cookies from 'js-cookie';

/**
 * Cookies written by Segment's analytics.js core. These are the identifiers the German
 * Competition Centre flagged in CAP-4147 — after the visitor rejects consent they must
 * be *gone*, not merely no longer updated.
 */
const TRACKING_COOKIES = [
    'ajs_anonymous_id',
    'ajs_user_id',
    'ajs_group_id',
    'ajs_group_properties',
];

/**
 * Exact storage keys holding *Segment's* tracking identity.
 *
 * Applied to both localStorage and sessionStorage: Segment mirrors `ajs_anonymous_id` /
 * `ajs_user_id` into sessionStorage as well as into cookies and localStorage (verified in
 * the browser), and leaving either copy behind means the identifier the cease-and-desist
 * names is still on the visitor's device after they reject.
 *
 * Perspective's own `perspective__user__id` is deliberately absent — see the note on
 * `purgeTrackingArtifacts`.
 */
const TRACKING_IDENTITY_KEYS = [
    'ajs_anonymous_id',
    'ajs_user_id',
    'ajs_group_id',
    'ajs_group_properties',
    'ajs_traits',
];

const RETRY_QUEUE_SUFFIXES = ['ack', 'inProgress', 'queue', 'reclaimStart', 'reclaimEnd'];

/**
 * The @segment/localstorage-retry queue, created as `new Queue('perspective', ...)` in
 * global/core/queue.ts. It holds not-yet-delivered /ps-fnl-events payloads, i.e. visitor
 * data, in localStorage — so it has to go when consent is withdrawn.
 *
 * The library inserts a per-tab UUID between the queue name and the suffix, producing keys
 * like `perspective.7734c7c5-be6d-44b5-9a4b-5eb8b73f7921.queue`. Our cookie policy documents
 * them as plain `perspective.queue` / `perspective.ack` — that is not what actually lands in
 * localStorage (verified in the browser), so match on the suffix to cover both shapes.
 */
const isRetryQueueKey = (key: string): boolean =>
    key.startsWith('perspective.') &&
    RETRY_QUEUE_SUFFIXES.some((suffix) => key.endsWith(`.${suffix}`));

const safeKeys = (storage: Storage): string[] => {
    try {
        const keys: string[] = [];

        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);

            if (key !== null) {
                keys.push(key);
            }
        }

        return keys;
    } catch {
        // Storage unavailable (Safari private mode, cross-origin iframe)
        return [];
    }
};

const safeRemove = (storage: Storage, key: string): void => {
    try {
        storage.removeItem(key);
    } catch {
        // Storage unavailable
    }
};

/**
 * Segment sets its cookies on the registrable domain, so a funnel served from
 * `go.example.de` gets cookies scoped to `.example.de`. js-cookie only deletes a cookie
 * when the delete request's domain matches the one it was written with, so walk every
 * candidate domain from the full host up to the registrable domain.
 */
const cookieDomainCandidates = (): (string | undefined)[] => {
    // `undefined` covers the host-only cookie (no explicit domain attribute).
    const domains: (string | undefined)[] = [undefined];
    const hostname = window.location?.hostname;

    if (!hostname) {
        return domains;
    }

    const parts = hostname.split('.');

    for (let i = 0; i < parts.length - 1; i++) {
        const domain = parts.slice(i).join('.');
        domains.push(domain, `.${domain}`);
    }

    return domains;
};

const removeCookie = (name: string): void => {
    for (const domain of cookieDomainCandidates()) {
        try {
            Cookies.remove(name, domain ? { path: '/', domain } : { path: '/' });
        } catch {
            // Ignore and keep trying the remaining domain scopes
        }
    }
};

/**
 * Delete the cookies and storage entries created by *Segment's* analytics.js.
 *
 * Called when the visitor denies consent (CAP-4147). Originally this removed Perspective's
 * own session state too, because rejecting meant no measurement at all. That is no longer
 * true: a rejecting visitor is now measured first-party (see `isFirstPartyTrackingAllowed`),
 * so the funnel's own session keys have to survive — `denyConsent()` runs this on *every*
 * load, including reloads, so purging them would restart the visitor's session on every
 * page refresh and inflate the visit count.
 *
 * Nothing *persistent* survives a rejection. localStorage is cleared of Segment's
 * identifiers, of Perspective's own visitor id, of the retry queue and of the CLO-2097 resume
 * pointer — a rejecting visitor keeps their visitor id in sessionStorage instead (see
 * `TrackingService.visitorIdStore`), so removing the localStorage copy here is what makes that
 * switch actually take effect for someone who accepted on an earlier visit.
 *
 * sessionStorage is deliberately left alone. It dies with the tab, it is all first-party, and
 * the funnel's own analytics needs it within the visit — `denyConsent()` runs on *every* load,
 * so purging it would restart the visit on each page refresh and inflate the visit count:
 * - `perspective__user__id`             — the visitor id, tab-scoped after a rejection
 * - `perspective_campaign_session_`     — the funnel session id
 * - `perspective_seen_non_homepage_`    — session regeneration bookkeeping
 * - `<sessionId>_user_traits`           — the session's accumulated traits
 * - `perspective.split-testing-variant.`— the A/B assignment, which must not flip mid-visit
 * - `perspective.result-mapping.`       — quiz answer → result page mapping
 * - `perspective.placeholders.`         — values that fill placeholders in funnel content
 *
 * `perspective.tracking-preferences.` stays in localStorage: it is the consent decision
 * itself, and forgetting it would re-ask on every visit.
 *
 * `RESUME_PREFIX` is the one first-party session entry that does *not* stay. It is in
 * localStorage rather than sessionStorage precisely so it outlives the tab, which makes it
 * the only one a rejection has to reach.
 */
export const purgeTrackingArtifacts = (): void => {
    if (typeof window === 'undefined') return;

    TRACKING_COOKIES.forEach(removeCookie);

    try {
        const localStorage = window.localStorage;

        TRACKING_IDENTITY_KEYS.forEach((key) => safeRemove(localStorage, key));
        safeRemove(localStorage, USER_ID_KEY);

        safeKeys(localStorage)
            .filter((key) => isRetryQueueKey(key) || key.startsWith(RESUME_PREFIX))
            .forEach((key) => safeRemove(localStorage, key));
    } catch {
        // localStorage unavailable
    }

    // Segment mirrors its identity keys into sessionStorage as well. Only those — the
    // first-party entries listed above stay.
    try {
        TRACKING_IDENTITY_KEYS.forEach((key) => safeRemove(window.sessionStorage, key));
    } catch {
        // sessionStorage unavailable
    }

    // Drop events buffered by the analytics.js stub queue so a later consent grant
    // cannot retroactively flush anything collected while consent was pending.
    if (Array.isArray(window.analytics_queue)) {
        window.analytics_queue.length = 0;
    }

    if (Array.isArray(window.ps_queue)) {
        window.ps_queue.length = 0;
    }

    // Same reasoning for the events the first-party path staged for the integrations
    // (CAP-4405): they never reached the stub queue, so they need dropping separately.
    clearDeferredIntegrationEvents();
};
